import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectDataMatchId, selectDataPatient, selectDataUser } from 'connect/selectors';

import { useHookResult } from 'utils/customHooks/use-hook-result';

import { useOfferService } from './OfferService';

interface AnalyticsServiceParams {
  offerService: any;
  user: any;
  patient: any;
  matchId: number;
}

interface AnalyticsData {
  userId?: number;
  matchId?: number;
  offerName?: string;
  offerValue?: string;
  offerType?: string;
}

class AnalyticsService {
  _offerService: AnalyticsServiceParams['offerService'];
  _user: AnalyticsServiceParams['user'];
  _patient: AnalyticsServiceParams['patient'];
  _matchId: AnalyticsServiceParams['matchId'];
  _analyticsData: AnalyticsData;

  constructor({ offerService, user, patient, matchId }: AnalyticsServiceParams) {
    this._offerService = offerService;
    this._user = user;
    this._patient = patient;
    this._matchId = matchId;
    this._analyticsData = {
      userId: user?.id,
      matchId,
      offerName: offerService.offerName,
      offerValue: offerService.offerValue,
      offerType: offerService.offerType,
    };
  }

  get analyticsData() {
    const userId = this._user?.id;
    const matchId = this._matchId;
    const offerName = this._offerService.offerName;
    const offerValue = this._offerService.offerValue;
    const offerType = this._offerService.offerType;

    // Prevent unnecessary re-renders
    const hasAnalyticsDataChanged =
      this._analyticsData.userId !== userId
      || this._analyticsData.matchId !== matchId
      || this._analyticsData.offerName !== offerName
      || this._analyticsData.offerValue !== offerValue
      || this._analyticsData.offerType !== offerType;

    if (hasAnalyticsDataChanged) {
      this._analyticsData = {
        userId,
        matchId,
        offerName,
        offerValue,
        offerType,
      };
    }
    return this._analyticsData;
  }
}

export const useAnalyticsService = () => {
  const offerService = useOfferService();

  const user = useSelector(selectDataUser);
  const patient = useSelector(selectDataPatient);
  const matchId = useSelector(selectDataMatchId);

  const createServiceParams = useCallback(() => ({
    offerService,
    user,
    patient,
    matchId,
  }), [offerService, user, patient, matchId]);

  const serviceParams = useHookResult(createServiceParams());

  const [service, setService] = useState(new AnalyticsService(serviceParams));

  useEffect(
    () => setService(new AnalyticsService(serviceParams)),
    [serviceParams],
  );

  return useHookResult(service);
};
